import { clone, EditableItemContextType, hasValue } from "@repo/helpers";
import { keyPair } from "@repo/models";
import { createContext, ReactNode, useCallback, useContext } from "react";

import { useCopiedProperties } from "../helpers/copiedPropertiesHook";
import { traverseProperty, traversePropertyUpdate } from "../helpers/dynamicProperties";
import { MappedComponent } from "../models/mappedComponent";
import { useDynamicSectionContext } from "./dynamicSectionContext";
import { useTemplateDataContext } from "./templateDataContext";

export interface EditDataContextType {
	updateProperty: (id: string, value: any, parentComponent?: string, dontEdit?: boolean) => Promise<any>;
	updateProperties: (properties: keyPair[], parentComponent?: string, dontEdit?: boolean) => Promise<any>;
	toggleCopiedProperties: (component: MappedComponent) => Promise<void>;
}

const defaultContextValues = {
	updateProperty: undefined,
	updateProperties: undefined,
	toggleCopiedProperties: undefined,
} as EditDataContextType;

type DataKey = "enrollment" | "repFieldAudit" | "lead";

interface EditDataProps {
	children: ReactNode;
	applyBusinessLogic: (
		updateValueInternal: (id: string, value: any, newState: any, parentComponent?: string) => Promise<any>,
	) => (id: string, value: any, oldValue: any, newState: any, parentComponent?: string) => Promise<any>;
	postUpdate: (newState: any) => void;
	itemContext: EditableItemContextType<any>;
	dontEdit?: boolean;
	initialized: boolean;
	dataKey: DataKey;
}

export const EditDataContext = createContext<EditDataContextType>(defaultContextValues);
export const useEditDataContext = () => useContext(EditDataContext);
export function EditDataProvider(props: EditDataProps) {
	const { children, postUpdate, initialized, dataKey } = props;
	const applyBusinessLogic = useCallback(props.applyBusinessLogic(updateValueInternal), []);
	const { templateData } = useTemplateDataContext();
	const { item, editItem, updateItem, pendingChanges } = props.itemContext;
	const { sections: mappedSections } = useDynamicSectionContext();

	const updateProperties = useCallback(
		async function (properties: keyPair[], parentComponent?: string, dontEdit?: boolean) {
			if (!initialized) return;
			let changed = false;
			let newState = clone(templateData);

			for (const property of properties) {
				const result = await updateValues(property.id as string, property.value, newState, parentComponent);
				changed = changed || result.changed;
				newState = result.newState;
			}

			const data = newState[dataKey];

			if (changed && !dontEdit) editItem(data);
			else updateItem(data);

			postUpdate(newState);
		},
		[initialized, templateData, editItem, updateItem, postUpdate],
	);

	const updateProperty = useCallback(
		async function (value: any, id: string, parentComponent?: string, dontEdit?: boolean) {
			await updateProperties([{ id, value }], parentComponent, dontEdit);
		},
		[updateProperties],
	);

	const { copiedProperties, toggleCopiedProperties } = useCopiedProperties(
		[item],
		hasValue(templateData) && !pendingChanges,
		templateData,
		mappedSections,
		updateProperty,
		editItem,
	);

	async function updateValueInternal<TTemplateData = any>(
		id: string,
		value: any,
		currentState: TTemplateData,
		parentComponent?: string,
	) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return newState ?? currentState;
	}

	async function updateValues<TTemplateData = any>(
		id: string,
		value: any,
		newState: TTemplateData,
		parentComponent?: string,
	) {
		const oldValue = traverseProperty(id, newState);
		if (oldValue === value) return { newState, changed: false };
		newState = traversePropertyUpdate(id, newState, value);
		if (typeof oldValue === "object" && oldValue && value) {
			//If object, check child properties
			let matches = true;
			for (const prop of Object.keys({ ...oldValue, ...value })) {
				if (!hasValue(oldValue[prop]) && !hasValue(value[prop])) matches = true;
				else if (oldValue[prop] === value[prop]) matches = true;
				else {
					matches = false;
					break;
				}
			}
			if (matches) return { newState, changed: false };
		}

		const changed = oldValue !== value;
		newState = await applyBusinessLogic(id, value, oldValue, newState, parentComponent);

		newState = propagateCopiedProps(id, value, newState, parentComponent);
		return { changed, newState };
	}

	function propagateCopiedProps<TTemplateData = any>(
		id: string,
		value: any,
		newState: TTemplateData,
		parentComponent?: string,
	) {
		if (copiedProperties.has(id)) {
			copiedProperties.getValue(id).forEach((name) => (newState = traversePropertyUpdate(name, newState, value)));
		}
		parentComponent = parentComponent ?? id;

		if (parentComponent && copiedProperties.has(parentComponent)) {
			const parentValue = {
				...(traverseProperty(parentComponent, newState) as any),
			};
			parentValue.matches = true;
			copiedProperties
				.getValue(parentComponent)
				.forEach((name) => (newState = traversePropertyUpdate(name, newState, parentValue)));
		}
		return newState;
	}

	return (
		<EditDataContext.Provider
			value={{
				updateProperty,
				updateProperties,
				toggleCopiedProperties,
			}}
		>
			{children}
		</EditDataContext.Provider>
	);
}
